import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usersAPI from '../../api/users';
import { useAuth } from '../../contexts/AuthContext';
import { getAuth } from 'firebase/auth';

export default function EditProfile({ onClose, onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    phone: user.phone || '',
    profileDescription: user.profileDescription || '',
    profileImage: user.profileImage || '',
    address: {
      city: user.address?.city || '',
      state: user.address?.state || '',
      zip: user.address?.zip || '',
      country: user.address?.country || ''
    },
    socialLinks: user.socialLinks || {},
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('socialLinks.')) {
      const platform = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        socialLinks: { ...prev.socialLinks, [platform]: value }
      }));
    } else if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken(); // ✅ Firebase ID token

      const res = await fetch('/api/uploads/profile', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}` // ✅ Secure upload
        }
      });

      const data = await res.json();
      setForm((prev) => ({ ...prev, profileImage: data.url }));
      setUploading(false);
    } catch (err) {
      console.error('Image upload failed:', err);
      setUploading(false);
      setError('Image upload failed.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedProfile = await usersAPI.updateProfile(form);
      setSuccess('✅ Profile updated successfully!');
      setError('');
      onUpdate(updatedProfile.data);
      onClose();
    } catch (err) {
      console.error('❌ Update failed:', err);
      setError('Something went wrong.');
      setSuccess('');
    }
  };

  return (
    <div className="max-w-xl mx-auto p-6 overflow-y-auto h-full">
      <h1 className="text-2xl font-bold mb-4">Edit Profile</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-medium">First Name</label>
          <input name="firstName" value={form.firstName} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Last Name</label>
          <input name="lastName" value={form.lastName} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Phone</label>
          <input name="phone" value={form.phone} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Bio</label>
          <textarea name="profileDescription" value={form.profileDescription} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>

        <div>
          <label className="block font-medium">City</label>
          <input name="address.city" value={form.address.city} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">State</label>
          <input name="address.state" value={form.address.state} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Zip</label>
          <input name="address.zip" value={form.address.zip} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Country</label>
          <input name="address.country" value={form.address.country} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>

        <div>
          <label className="block font-medium">Instagram</label>
          <input name="socialLinks.instagram" value={form.socialLinks.instagram || ''} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>
        <div>
          <label className="block font-medium">Facebook</label>
          <input name="socialLinks.facebook" value={form.socialLinks.facebook || ''} onChange={handleChange} className="w-full border p-2 rounded" />
        </div>

        <div>
          <label className="block font-medium">Profile Image</label>
          <input type="file" onChange={handleImageUpload} className="w-full" />
          {uploading && <p className="text-sm text-blue-500">Uploading...</p>}
          {form.profileImage && <img src={form.profileImage} alt="Preview" className="w-24 h-24 rounded-full mt-2 object-cover" />}
        </div>

        <div className="flex gap-2">
          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Save Changes
          </button>
          <button type="button" onClick={onClose} className="bg-gray-400 text-white px-4 py-2 rounded hover:bg-gray-500">
            Cancel
          </button>
        </div>

        {success && <p className="text-green-600 text-sm mt-2">{success}</p>}
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </form>
    </div>
  );
}